"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import {
  Avatar,
  Badge,
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  Menu,
  Portal,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { LuEllipsisVertical } from "react-icons/lu";
import { LuPencil, LuTrash2 } from "react-icons/lu";
import { useMessage } from "@/app/context/MessageProvider";
import { getCommentAuthorDisplayName } from "@/app/lib/comment-author-name";
import { CommentVoteButtons } from "@/components/ux/CommentUpvoteButton";

type CommentStatus = "pending" | "approved" | "rejected";

export type BlogVisibleComment = {
  id: string;
  authorId: string;
  authorName: string;
  authorImage?: string | null;
  content: string;
  vote: {
    up: number;
    down: number;
    userVote: "up" | "down" | null;
  };
  status: CommentStatus;
  createdAt: string;
};

type BlogCommentsSectionProps = {
  slug: string;
  comments: BlogVisibleComment[];
  currentUserId: string | null;
};

type CommentApiResponse = {
  success: boolean;
  error?: string;
};

type CommentVoteApiResponse = {
  success: boolean;
  vote?: {
    up: number;
    down: number;
    userVote: "up" | "down" | null;
  };
  error?: string;
};

const MAX_COMMENT_LENGTH = 100;

export function BlogCommentsSection({
  slug,
  comments,
  currentUserId,
}: BlogCommentsSectionProps) {
  const router = useRouter();
  const { notify } = useMessage();
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState("");
  const [isMutating, setIsMutating] = useState(false);
  const [isVotingById, setIsVotingById] = useState<Record<string, boolean>>({});
  const [upvoteStateById, setUpvoteStateById] = useState<
    Record<string, { up: number; down: number; userVote: "up" | "down" | null }>
  >(() =>
    comments.reduce<Record<string, { up: number; down: number; userVote: "up" | "down" | null }>>(
      (acc, comment) => {
        acc[comment.id] = {
          up: comment.vote.up,
          down: comment.vote.down,
          userVote: comment.vote.userVote,
        };
        return acc;
      },
      {},
    ),
  );

  useEffect(() => {
    setUpvoteStateById(
      comments.reduce<Record<string, { up: number; down: number; userVote: "up" | "down" | null }>>(
        (acc, comment) => {
          acc[comment.id] = {
            up: comment.vote.up,
            down: comment.vote.down,
            userVote: comment.vote.userVote,
          };
          return acc;
        },
        {},
      ),
    );
  }, [comments]);

  const visibleComments = useMemo(
    () =>
      comments.filter((comment) => {
        const isOwnComment = currentUserId !== null && comment.authorId === currentUserId;
        return comment.status === "approved" || isOwnComment;
      }).sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [comments, currentUserId],
  );

  const startEditing = (comment: BlogVisibleComment) => {
    setEditingCommentId(comment.id);
    setEditedContent(comment.content);
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    setEditedContent("");
  };

  const handleSaveEdit = (commentId: string) => {
    const trimmed = editedContent.trim();
    if (!trimmed) {
      notify({
        mode: "error",
        description: "Comment cannot be empty.",
      });
      return;
    }
    if (trimmed.length > MAX_COMMENT_LENGTH) {
      notify({
        mode: "error",
        description: `Comment must be ${MAX_COMMENT_LENGTH} characters or less.`,
      });
      return;
    }
    if (isMutating) return;

    setIsMutating(true);

    void (async () => {
      try {
        const response = await fetch(`/api/blog/${slug}/comments/${commentId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content: trimmed }),
        });
        const payload = (await response.json().catch(() => null)) as CommentApiResponse | null;

        if (!response.ok || !payload?.success) {
          notify({
            mode: "error",
            description: payload?.error ?? "Unable to update comment right now.",
          });
          return;
        }

        notify({
          mode: "regular",
          title: "Comment updated",
          description: "Your edited comment is pending review again.",
        });
        cancelEditing();
        router.refresh();
      } catch (error) {
        console.error("[BLOG_COMMENT_EDIT]", error);
        notify({
          mode: "error",
          description: "Unable to update comment right now.",
        });
      } finally {
        setIsMutating(false);
      }
    })();
  };

  const handleDeleteComment = (commentId: string) => {
    if (isMutating) return;
    const shouldDelete = window.confirm("Delete this comment?");
    if (!shouldDelete) return;

    setIsMutating(true);

    void (async () => {
      try {
        const response = await fetch(`/api/blog/${slug}/comments/${commentId}`, {
          method: "DELETE",
        });
        const payload = (await response.json().catch(() => null)) as CommentApiResponse | null;

        if (!response.ok || !payload?.success) {
          notify({
            mode: "error",
            description: payload?.error ?? "Unable to delete comment right now.",
          });
          return;
        }

        notify({
          mode: "success",
          description: "Comment deleted.",
        });
        if (editingCommentId === commentId) {
          cancelEditing();
        }
        router.refresh();
      } catch (error) {
        console.error("[BLOG_COMMENT_DELETE]", error);
        notify({
          mode: "error",
          description: "Unable to delete comment right now.",
        });
      } finally {
        setIsMutating(false);
      }
    })();
  };

  const handleVoteToggle = (commentId: string, nextVote: "up" | "down") => {
    if (!currentUserId) {
      notify({
        mode: "error",
        title: "Sign in required",
        description: "Sign in to vote on comments.",
      });
      return;
    }

    if (isVotingById[commentId]) return;

    const previousState = upvoteStateById[commentId] ?? { up: 0, down: 0, userVote: null };

    setUpvoteStateById((prev) => {
      const current = prev[commentId] ?? { up: 0, down: 0, userVote: null };
      let nextUp = current.up;
      let nextDown = current.down;
      const nextUserVote = current.userVote === nextVote ? null : nextVote;

      if (current.userVote === "up") {
        nextUp = Math.max(0, nextUp - 1);
      } else if (current.userVote === "down") {
        nextDown = Math.max(0, nextDown - 1);
      }

      if (nextUserVote === "up") {
        nextUp += 1;
      } else if (nextUserVote === "down") {
        nextDown += 1;
      }

      return {
        ...prev,
        [commentId]: {
          up: nextUp,
          down: nextDown,
          userVote: nextUserVote,
        },
      };
    });

    setIsVotingById((prev) => ({ ...prev, [commentId]: true }));

    const optimistic = upvoteStateById[commentId] ?? previousState;
    const resolvedNextVote = optimistic.userVote === nextVote ? null : nextVote;

    void (async () => {
      try {
        const response = await fetch(`/api/blog/${slug}/comments/${commentId}/vote`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ vote: resolvedNextVote }),
        });
        const payload = (await response.json().catch(() => null)) as CommentVoteApiResponse | null;

        if (!response.ok || !payload?.success || !payload.vote) {
          setUpvoteStateById((prev) => ({ ...prev, [commentId]: previousState }));
          notify({
            mode: "error",
            description: payload?.error ?? "Unable to update vote right now.",
          });
          return;
        }

        setUpvoteStateById((prev) => ({ ...prev, [commentId]: payload.vote! }));
      } catch (error) {
        console.error("[BLOG_COMMENT_VOTE]", error);
        setUpvoteStateById((prev) => ({ ...prev, [commentId]: previousState }));
        notify({
          mode: "error",
          description: "Unable to update vote right now.",
        });
      } finally {
        setIsVotingById((prev) => ({ ...prev, [commentId]: false }));
      }
    })();
  };

  return (
    <Box
      id="comments"
      tabIndex={-1}
      scrollMarginTop={{ base: "92px", md: "118px" }}
      border="1px solid"
      borderColor="blackAlpha.200"
      rounded="xl"
      p={{ base: 4, md: 5 }}
      bg="white"
      _dark={{ bg: "blackAlpha.400", borderColor: "whiteAlpha.300" }}
      _focusVisible={{
        outline: "2px solid",
        outlineColor: "teal.400",
        outlineOffset: "3px",
      }}
    >
      <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} mb={2}>
        Comments
      </Heading>
      {visibleComments.length > 0 ? (
        <Stack gap={3}>
          {visibleComments.map((comment) => {
            const isOwnComment = currentUserId !== null && comment.authorId === currentUserId;
            const authorDisplayName = getCommentAuthorDisplayName(comment.authorName);
            const createdAtLabel = new Intl.DateTimeFormat("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            }).format(new Date(comment.createdAt));
            const isEditing = editingCommentId === comment.id;

            return (
              <Box
                key={comment.id}
                border="1px solid"
                borderColor="gray.200"
                rounded="lg"
                p={2}
                bg="gray.50"
                _dark={{ bg: "blackAlpha.300", borderColor: "whiteAlpha.300" }}
              >
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <Avatar.Root size="xs" shape="full" boxSize="18px" mt="1px">
                    <Avatar.Fallback name={authorDisplayName} />
                    <Avatar.Image src={comment.authorImage ?? undefined} />
                  </Avatar.Root>
                  <Stack gap={1} flex="1">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={2}
                      flexWrap="wrap"
                    >
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color="gray.600"
                        _dark={{ color: "gray.400" }}
                        fontFamily="sans-serif"
                      >
                        {authorDisplayName}
                        {isOwnComment ? " (you)" : ""}
                      </Text>
                      <HStack gap={1.5}>
                        {isOwnComment && comment.status !== "approved" ? (
                          <Badge
                            colorPalette={comment.status === "pending" ? "orange" : "red"}
                            variant="solid"
                            textTransform="capitalize"
                            fontSize="2xs"
                            px={1.5}
                            py={0.5}
                          >
                            {comment.status}
                          </Badge>
                        ) : null}
                        <Text fontSize="2xs" color="gray.600" _dark={{ color: "gray.400" }}>
                          {createdAtLabel}
                        </Text>
                        {isOwnComment && !isEditing ? (
                          <Menu.Root positioning={{ placement: "bottom-end" }}>
                            <Menu.Trigger asChild>
                              <IconButton
                                aria-label="Comment actions"
                                size="2xs"
                                minW="20px"
                                h="20px"
                                variant="ghost"
                                color="gray.600"
                                _dark={{ color: "gray.400" }}
                                disabled={isMutating}
                              >
                                <LuEllipsisVertical size={14} />
                              </IconButton>
                            </Menu.Trigger>
                            <Portal>
                              <Menu.Positioner>
                                <Menu.Content
                                  minW="auto"
                                  p="1"
                                  rounded="md"
                                  display="flex"
                                  flexDirection="row"
                                  gap="1"
                                  alignItems="center"
                                >
                                  <Menu.Item
                                    value={`edit-${comment.id}`}
                                    onClick={() => startEditing(comment)}
                                    px="1"
                                    py="1"
                                    minH="24px"
                                    justifyContent="center"
                                    w="auto"
                                    display="inline-flex"
                                    flex="0 0 auto"
                                  >
                                    <LuPencil size={14} />
                                  </Menu.Item>
                                  <Menu.Item
                                    value={`delete-${comment.id}`}
                                    color="red.600"
                                    onClick={() => handleDeleteComment(comment.id)}
                                    px="1"
                                    py="1"
                                    minH="24px"
                                    justifyContent="center"
                                    w="auto"
                                    display="inline-flex"
                                    flex="0 0 auto"
                                  >
                                    <LuTrash2 size={14} />
                                  </Menu.Item>
                                </Menu.Content>
                              </Menu.Positioner>
                            </Portal>
                          </Menu.Root>
                        ) : null}
                      </HStack>
                    </Box>

                    {isEditing ? (
                      <Stack gap={1.5}>
                        <Text fontSize="2xs" color="gray.600" _dark={{ color: "gray.400" }} textAlign="right">
                          {editedContent.length}/{MAX_COMMENT_LENGTH}
                        </Text>
                        <Textarea
                          value={editedContent}
                          onChange={(event) => setEditedContent(event.target.value)}
                          maxLength={MAX_COMMENT_LENGTH}
                          size="xs"
                          minH="68px"
                          fontSize="xs"
                          borderColor="teal.400"
                          _focusVisible={{ borderColor: "teal.400" }}
                          _hover={{ borderColor: "teal.400" }}
                          bg="white"
                          _dark={{ bg: "blackAlpha.300" }}
                        />
                        <HStack justify="flex-end" gap={1}>
                          <Button
                            size="2xs"
                            variant="outline"
                            onClick={cancelEditing}
                            disabled={isMutating}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="2xs"
                            colorPalette="teal"
                            onClick={() => handleSaveEdit(comment.id)}
                            disabled={isMutating || !editedContent.trim()}
                          >
                            Save
                          </Button>
                        </HStack>
                      </Stack>
                    ) : (
                      <Stack gap={1.5}>
                        <Text
                          fontSize="xs"
                          lineHeight="1.6"
                          color="gray.600"
                          _dark={{ color: "gray.300" }}
                          fontFamily="Inter, sans-serif"
                        >
                          {comment.content}
                        </Text>
                        <HStack justify="flex-end">
                          <CommentVoteButtons
                            upCount={upvoteStateById[comment.id]?.up ?? comment.vote.up}
                            downCount={upvoteStateById[comment.id]?.down ?? comment.vote.down}
                            userVote={upvoteStateById[comment.id]?.userVote ?? comment.vote.userVote}
                            disabled={isMutating || isVotingById[comment.id]}
                            onVote={(vote) => handleVoteToggle(comment.id, vote)}
                          />
                        </HStack>
                      </Stack>
                    )}

                  </Stack>
                </Box>
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }} lineHeight="1.7">
          No comments yet. Start the conversation.
        </Text>
      )}
    </Box>
  );
}
